/**
 * UCM (Unit Control Message) 报文生成 API Service
 * 提供UCM报文生成和历史记录管理的所有API接口封装
 */

import apiClient from "./axios";

// 基础路径
const BASE_PATH = "/v1/uld/ucm";

/**
 * UCM报文生成
 */

// 生成UCM报文
export const generateUcm = (data) => {
    return apiClient.post(`${BASE_PATH}/generate`, data);
};

/**
 * UCM历史记录查询
 */

// 获取UCM历史记录列表（分页查询）
export const getUcmHistory = (params = {}) => {
    return apiClient.get(`${BASE_PATH}/history`, { params });
};

// 获取单个UCM历史记录详情
export const getUcmById = (historyId) => {
    return apiClient.get(`${BASE_PATH}/history/${historyId}`);
};

// 删除UCM历史记录
export const deleteUcmHistory = (historyId) => {
    return apiClient.delete(`${BASE_PATH}/history/${historyId}`);
};

/**
 * UCM 入向解析确认（待确认记录）
 */

// 入向解析路径
const INBOUND_PATH = `${BASE_PATH}/inbound`;

// 人工粘贴报文文本，解析成待确认记录（source=manual）
export const parseUcmInbound = (data) => {
    return apiClient.post(`${INBOUND_PATH}/parse`, data);
};

// 待确认记录列表（分页 + 状态/方向筛选）
// 默认按"同源（同邮件 / 同原文）"合并展示：一封 UCM 报文里同时含 IN+OUT 段时，
// 列表合成一行，方向列展示多标签；详情抽屉通过顶部切换器在段之间切换。
// 调用方可显式传 group_by_source=false 关闭分组（排错或老逻辑兼容）。
export const getUcmPendingList = (params = {}) => {
    const merged = { group_by_source: true, ...params };
    return apiClient.get(`${INBOUND_PATH}/pending`, { params: merged });
};

// 待确认记录详情（含报文原文与逐块预览）
export const getUcmPendingById = (pendingId) => {
    return apiClient.get(`${INBOUND_PATH}/pending/${pendingId}`);
};

// 按写台账批次号反查来源 UCM 报文（H6 板流转历史→报文回链）
export const getUcmInboundByBatch = (batchIdentifier) => {
    return apiClient.get(`${INBOUND_PATH}/by-batch`, {
        params: { batch_identifier: batchIdentifier },
    });
};

// 确认写台账（出港挂航班 / 进港落目的站）
// force=true：整封强制确认，连位置不符 / 已外放 / 退役停用的「需强制」块一并强制写入（长按 5s 后调用）
export const confirmUcmPending = (pendingId, { force = false } = {}) => {
    return apiClient.post(
        `${INBOUND_PATH}/pending/${pendingId}/confirm`,
        null,
        force ? { params: { force: true } } : undefined,
    );
};

// 忽略一条待确认 / 解析失败记录
export const ignoreUcmPending = (pendingId) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/ignore`);
};

// 恢复整条已忽略记录，并按当前航班 / ULD 台账刷新预览
export const reopenUcmPending = (pendingId) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/reopen`);
};

// 重新匹配航班（flightOpId 为空=按报文自动重匹配；给定=手动指定该航班）
export const rematchUcmPending = (pendingId, flightOpId = null) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/rematch`, {
        flight_op_id: flightOpId ?? null,
    });
};

// 手动指定航班的候选 / 搜索（传 keyword=航班号关键字则不限日期模糊搜；为空给报文附近建议）
export const getUcmFlightCandidates = (pendingId, keyword) => {
    const kw = (keyword || '').trim();
    return apiClient.get(`${INBOUND_PATH}/pending/${pendingId}/flight-candidates`, {
        params: kw ? { keyword: kw } : {},
    });
};

// 确认前预览：逐块处置（可直接写 / 需强制[位置·外放·退役] / 待建档 / 已了结）+ 三色计数
export const classifyUcmPending = (pendingId) => {
    return apiClient.get(`${INBOUND_PATH}/pending/${pendingId}/classify`);
};

// 单块写台账（只把第 index 块写入台账）
export const confirmUcmPendingItem = (pendingId, index) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/items/${index}/confirm`);
};

// 单块强制通过（长按 5s 后调用）：位置不符跳一致性 + 已外放自动撤销 + 退役/停用自动恢复
export const forceConfirmUcmPendingItem = (pendingId, index) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/items/${index}/force-confirm`);
};

// 批量强制通过（长按 5s 后调用）：indices 为空时对所有"需强制"的块一次性强制通过
export const forceConfirmUcmPending = (pendingId, indices = []) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/force-confirm`, {
        indices: indices || [],
    });
};

// 单块忽略（把第 index 块标为忽略，不写台账）
export const ignoreUcmPendingItem = (pendingId, index) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/items/${index}/ignore`);
};

// 撤销单块忽略，只刷新这一块的当前台账与拟改位置
export const reopenUcmPendingItem = (pendingId, index) => {
    return apiClient.post(`${INBOUND_PATH}/pending/${pendingId}/items/${index}/reopen`);
};

// 改板号（只改第 index 块的板号，重算已知/未知，不动报文原文）
export const updateUcmPendingItemIdentifier = (pendingId, index, uldIdentifier) => {
    return apiClient.patch(`${INBOUND_PATH}/pending/${pendingId}/items/${index}`, {
        uld_identifier: uldIdentifier,
    });
};

// 改判运输方式（AIR / SURFACE / UNDECIDED 互改；可选传趟次号 / 改判依据）
export const updateUcmPendingTransportMode = (
    pendingId,
    { transportMode, surfaceTripNumber = null, reason = null } = {},
) => {
    return apiClient.patch(`${INBOUND_PATH}/pending/${pendingId}/transport-mode`, {
        transport_mode: transportMode,
        surface_trip_number: surfaceTripNumber,
        reason,
    });
};

// 导出所有API作为对象（用于enhancedApi增强）
const ucmApi = {
    generateUcm,
    getUcmHistory,
    getUcmById,
    deleteUcmHistory,
    // 入向解析确认
    parseUcmInbound,
    getUcmPendingList,
    getUcmPendingById,
    getUcmInboundByBatch,
    confirmUcmPending,
    ignoreUcmPending,
    reopenUcmPending,
    rematchUcmPending,
    getUcmFlightCandidates,
    classifyUcmPending,
    confirmUcmPendingItem,
    forceConfirmUcmPendingItem,
    forceConfirmUcmPending,
    ignoreUcmPendingItem,
    reopenUcmPendingItem,
    updateUcmPendingItemIdentifier,
    updateUcmPendingTransportMode,
};

export default ucmApi;
